import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import CommunityService from "./community_services";
import {Challenge, CommunityState} from "./types";

export const fetchReels = createAsyncThunk(
    'community/fetchReels',
    async (query: any, {rejectWithValue}): Promise<any> => {
        try {
            return await CommunityService.getReels(query);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchMoreReels = createAsyncThunk(
    'community/fetchMoreReels',
    async (query: any, {rejectWithValue}): Promise<any> => {
        try {
            return await CommunityService.getReels(query);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchReelsByCursor = createAsyncThunk(
    'community/fetchReelsByCursor',
    async (query: any, {rejectWithValue}) => {
        try {
            return await CommunityService.getReelsByCursor(query);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchMoreReelsByCursor = createAsyncThunk(
    'community/fetchMoreReelsByCursor',
    async (query: any, {rejectWithValue}): Promise<any> => {
        try {
            return await CommunityService.getReelsByCursor(query);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const likeReel = createAsyncThunk(
    'community/likeReel',
    async (reelId: string, {rejectWithValue}) => {
        try {
            return await CommunityService.likeReel(reelId);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const unlikeReel = createAsyncThunk(
    'community/unlikeReel',
    async (reelId: string, {rejectWithValue}) => {
        try {
            return await CommunityService.unlikeReel(reelId);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchChallenges = createAsyncThunk(
    'community/fetchChallenges',
    async (query: Record<string, any> | undefined, {rejectWithValue}) => {
        try {
            return await CommunityService.getChallenges(query || {});
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchUserChallenges = createAsyncThunk(
    'community/fetchUserChallenges',
    async (query: Record<string, any> | undefined, {rejectWithValue}) => {
        try {
            return await CommunityService.getUserChallenges(query || {});
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchChallengeByUuid = createAsyncThunk(
    'community/fetchChallengeByUuid',
    async (challengeUuid: string, {rejectWithValue}) => {
        try {
            return await CommunityService.getChallengeByUuid(challengeUuid);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchChallengeEntries = createAsyncThunk(
    'community/fetchChallengeEntries',
    async (payload: {challengeId: number; page?: number; limit?: number}, {rejectWithValue}) => {
        try {
            const result = await CommunityService.getChallengeEntries(payload.challengeId, {
                page: payload.page || 1,
                limit: payload.limit || 20,
            });
            return result.entries || [];
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const startChallengeFunding = createAsyncThunk(
    'community/startChallengeFunding',
    async (payload: {challengeId: number; provider?: string}, {rejectWithValue}) => {
        try {
            return await CommunityService.startChallengeFunding(payload.challengeId, payload.provider);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const createChallenge = createAsyncThunk(
    'community/createChallenge',
    async (payload: Record<string, any>, {rejectWithValue}) => {
        try {
            return await CommunityService.createChallenge(payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const updateChallenge = createAsyncThunk(
    'community/updateChallenge',
    async (payload: {challengeId: number; data: Record<string, any>}, {rejectWithValue}) => {
        try {
            return await CommunityService.updateChallenge(payload.challengeId, payload.data);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const submitChallengeEntry = createAsyncThunk(
    'community/submitChallengeEntry',
    async (payload: Record<string, any>, {rejectWithValue}) => {
        try {
            return await CommunityService.createChallengeEntry(payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchChallengeLeaderboard = createAsyncThunk(
    'community/fetchChallengeLeaderboard',
    async (challengeId: number, {rejectWithValue}) => {
        try {
            return await CommunityService.getChallengeLeaderboard(challengeId);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchChallengeInterests = createAsyncThunk(
    'community/fetchChallengeInterests',
    async (challengeId: number, {rejectWithValue}) => {
        try {
            return await CommunityService.getChallengeInterests(challengeId);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchUserChallengeInterest = createAsyncThunk(
    'community/fetchUserChallengeInterest',
    async (challengeId: number, {rejectWithValue}) => {
        try {
            return await CommunityService.getUserChallengeInterest(challengeId);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const expressChallengeInterest = createAsyncThunk(
    'community/expressChallengeInterest',
    async (challengeId: number, {rejectWithValue}) => {
        try {
            return await CommunityService.expressChallengeInterest(challengeId);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchChallengeModerators = createAsyncThunk(
    'community/fetchChallengeModerators',
    async (challengeId: number, {rejectWithValue}) => {
        try {
            return await CommunityService.getChallengeModerators(challengeId);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const assignChallengeModerator = createAsyncThunk(
    'community/assignChallengeModerator',
    async (payload: {challenge_id: number; user_email: string; weight: number}, {rejectWithValue}) => {
        try {
            await CommunityService.assignChallengeModerator(payload);
            return payload;
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const removeChallengeModerator = createAsyncThunk(
    'community/removeChallengeModerator',
    async (payload: {challenge_id: number; user_email: string}, {rejectWithValue}) => {
        try {
            await CommunityService.removeChallengeModerator(payload.challenge_id, payload.user_email);
            return payload;
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const voteChallengeEntry = createAsyncThunk(
    'community/voteChallengeEntry',
    async (payload: {entry_id: number; score: number; comment?: string}, {rejectWithValue}) => {
        try {
            return await CommunityService.voteChallengeEntry(payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const likeChallengeEntry = createAsyncThunk(
    'community/likeChallengeEntry',
    async (entryId: number, {rejectWithValue}) => {
        try {
            return await CommunityService.likeChallengeEntry(entryId);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const unlikeChallengeEntry = createAsyncThunk(
    'community/unlikeChallengeEntry',
    async (entryId: number, {rejectWithValue}) => {
        try {
            return await CommunityService.unlikeChallengeEntry(entryId);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const disqualifyChallengeEntry = createAsyncThunk(
    'community/disqualifyChallengeEntry',
    async (payload: {entry_id: number; reason: string}, {rejectWithValue}) => {
        try {
            return await CommunityService.disqualifyChallengeEntry(payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchDisqualifiedChallengeEntries = createAsyncThunk(
    'community/fetchDisqualifiedChallengeEntries',
    async (challengeId: number, {rejectWithValue}) => {
        try {
            return await CommunityService.getDisqualifiedChallengeEntries(challengeId);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const reviewChallengeEntry = createAsyncThunk(
    'community/reviewChallengeEntry',
    async (payload: {entry_id: number; comment: string; is_public?: boolean}, {rejectWithValue}) => {
        try {
            return await CommunityService.reviewChallengeEntry(payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchChallengeEntryReviews = createAsyncThunk(
    'community/fetchChallengeEntryReviews',
    async (entryId: number, {rejectWithValue}) => {
        try {
            return await CommunityService.getChallengeEntryReviews(entryId);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchChallengeModerationLogs = createAsyncThunk(
    'community/fetchChallengeModerationLogs',
    async (challengeId: number, {rejectWithValue}) => {
        try {
            return await CommunityService.getChallengeModerationLogs(challengeId);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

const initialState: CommunityState = {
    reels: [],
    reel: null,
    challenges: [],
    userChallenges: [],
    selectedChallenge: null,
    challengeEntries: [],
    challengeLeaderboard: [],
    challengeInterests: null,
    challengeModerators: [],
    challengeEntryReviewsByEntryId: {},
    challengeModerationLogs: [],
    disqualifiedChallengeEntries: [],
    hasExpressedChallengeInterest: null,
    hasMore: true,
    currentPage: 1,
    nextCursor: null,
    loading: false,
    challengeLoading: false,
    userChallengesLoading: false,
    challengeActionLoading: false,
    challengeModeratorActionLoading: false,
    challengeVoteActionLoading: false,
    challengeReviewActionLoading: false,
    challengeDisqualifyActionLoading: false,
    actionLoading: false,
    error: null,
    challengeError: null,
    challengeModeratorError: null,
    challengeVoteError: null,
    challengeReviewError: null,
    challengeDisqualifyError: null,
};

export const communitySlice = createSlice({
    name: 'community',
    initialState,
    reducers: {
        updateReel: (state, action) => {
            const reelId = action.payload.reel_id;
            const reelIndex = state.reels.findIndex((reel) => reel.uuid === reelId);
            if (reelIndex !== -1) {
                state.reels[reelIndex] = {
                    ...state.reels[reelIndex],
                    ...action.payload
                };
            }
        },
        clearSelectedChallenge: (state) => {
            state.selectedChallenge = null;
            state.challengeEntries = [];
            state.challengeLeaderboard = [];
            state.challengeInterests = null;
            state.challengeModerators = [];
            state.challengeEntryReviewsByEntryId = {};
            state.challengeModerationLogs = [];
            state.disqualifiedChallengeEntries = [];
            state.hasExpressedChallengeInterest = null;
            state.challengeActionLoading = false;
            state.challengeModeratorActionLoading = false;
            state.challengeVoteActionLoading = false;
            state.challengeReviewActionLoading = false;
            state.challengeDisqualifyActionLoading = false;
            state.challengeError = null;
            state.challengeModeratorError = null;
            state.challengeVoteError = null;
            state.challengeReviewError = null;
            state.challengeDisqualifyError = null;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchReels.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchReels.fulfilled, (state, action) => {
                state.loading = false;
                state.reels = action.payload.reels;
                state.hasMore = action.payload.pagination.hasMore;
                state.currentPage = action.payload.pagination.currentPage;
            })
            .addCase(fetchReels.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
        builder
            .addCase(fetchMoreReels.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchMoreReels.fulfilled, (state, action) => {
                state.loading = false;
                const newReels = action.payload.reels || [];
                const existingIds = new Set(state.reels.map(r => r.uuid));
                const deduped = newReels.filter((r: { uuid: string; }) => !existingIds.has(r.uuid));
                state.reels = [...state.reels, ...deduped];
                state.hasMore = action.payload.pagination.hasMore;
                state.currentPage = action.payload.pagination.currentPage;
            })
            .addCase(fetchMoreReels.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        builder
            .addCase(fetchReelsByCursor.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchReelsByCursor.fulfilled, (state, action) => {
                state.loading = false;
                const newReels = action.payload.reels || [];
                const existingIds = new Set(state.reels.map(r => r.uuid));
                const deduped = newReels.filter((r: { uuid: string; }) => !existingIds.has(r.uuid));
                state.reels = [...state.reels, ...deduped];
                state.nextCursor = action.payload.pagination?.nextCursor || null;
                state.hasMore = action.payload.pagination?.hasMore ?? false;
            })
            .addCase(fetchReelsByCursor.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        builder.addCase(fetchMoreReelsByCursor.pending, (state) => {
            state.loading = true;
            state.error = null;
        }).addCase(fetchMoreReelsByCursor.fulfilled, (state, action) => {
            state.loading = false;
            const newReels = action.payload.reels || [];
            const existingIds = new Set(state.reels.map(r => r.uuid));
            const deduped = newReels.filter((r: { uuid: string; }) => !existingIds.has(r.uuid));
            state.reels = [...state.reels, ...deduped];
            state.nextCursor = action.payload.pagination?.nextCursor || null;
            state.hasMore = action.payload.pagination?.hasMore ?? false;
        }).addCase(fetchMoreReelsByCursor.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        builder
            .addCase(likeReel.pending, (state) => {
                state.actionLoading = true;
                state.error = null;
            })
            .addCase(likeReel.fulfilled, (state, action) => {
                state.actionLoading = false;
                const reelId = action.payload.reel_id;
                const reelIndex = state.reels.findIndex((reel) => reel.uuid === reelId);
                if (reelIndex !== -1) {
                    state.reels[reelIndex].is_liked = true;
                    state.reels[reelIndex].likes += 1;
                }
            })
            .addCase(likeReel.rejected, (state, action) => {
                state.actionLoading = false;
                state.error = action.payload as string;
            });

        builder
            .addCase(unlikeReel.pending, (state) => {
                state.actionLoading = true;
                state.error = null;
            })
            .addCase(unlikeReel.fulfilled, (state, action) => {
                state.actionLoading = false;
                const reelId = action.payload.reel_id;
                const reelIndex = state.reels.findIndex((reel) => reel.uuid === reelId);
                if (reelIndex !== -1) {
                    state.reels[reelIndex].is_liked = false;
                    state.reels[reelIndex].likes -= 1;
                }
            })
            .addCase(unlikeReel.rejected, (state, action) => {
                state.actionLoading = false;
                state.error = action.payload as string;
            });

        builder
            .addCase(fetchChallenges.pending, (state) => {
                state.challengeLoading = true;
                state.challengeError = null;
            })
            .addCase(fetchChallenges.fulfilled, (state, action) => {
                state.challengeLoading = false;
                state.challenges = action.payload.challenges || [];
            })
            .addCase(fetchChallenges.rejected, (state, action) => {
                state.challengeLoading = false;
                state.challengeError = action.payload as string;
            });

        builder
            .addCase(fetchUserChallenges.pending, (state) => {
                state.userChallengesLoading = true;
                state.challengeError = null;
            })
            .addCase(fetchUserChallenges.fulfilled, (state, action) => {
                state.userChallengesLoading = false;
                state.userChallenges = action.payload.challenges || [];
            })
            .addCase(fetchUserChallenges.rejected, (state, action) => {
                state.userChallengesLoading = false;
                state.challengeError = action.payload as string;
            });

        builder
            .addCase(fetchChallengeByUuid.pending, (state) => {
                state.challengeLoading = true;
                state.challengeError = null;
            })
            .addCase(fetchChallengeByUuid.fulfilled, (state, action) => {
                state.challengeLoading = false;
                state.selectedChallenge = action.payload;
            })
            .addCase(fetchChallengeByUuid.rejected, (state, action) => {
                state.challengeLoading = false;
                state.challengeError = action.payload as string;
            });

        builder
            .addCase(fetchChallengeEntries.pending, (state) => {
                state.challengeLoading = true;
                state.challengeError = null;
            })
            .addCase(fetchChallengeEntries.fulfilled, (state, action) => {
                state.challengeLoading = false;
                state.challengeEntries = action.payload || [];
            })
            .addCase(fetchChallengeEntries.rejected, (state, action) => {
                state.challengeLoading = false;
                state.challengeError = action.payload as string;
            });

        builder
            .addCase(startChallengeFunding.pending, (state) => {
                state.challengeActionLoading = true;
                state.challengeError = null;
            })
            .addCase(startChallengeFunding.fulfilled, (state, action) => {
                state.challengeActionLoading = false;
                const challengeId = action.payload?.challenge_id;
                if (challengeId) {
                    const updateFundingStatus = (challenge: Challenge) =>
                        challenge.id === challengeId ? {...challenge, funding_status: 'funding'} : challenge;

                    state.challenges = state.challenges.map(updateFundingStatus);
                    state.userChallenges = state.userChallenges.map(updateFundingStatus);
                    if (state.selectedChallenge?.id === challengeId) {
                        state.selectedChallenge = {
                            ...state.selectedChallenge,
                            funding_status: 'funding',
                        };
                    }
                }
            })
            .addCase(startChallengeFunding.rejected, (state, action) => {
                state.challengeActionLoading = false;
                state.challengeError = action.payload as string;
            });

        builder
            .addCase(createChallenge.pending, (state) => {
                state.challengeActionLoading = true;
                state.challengeError = null;
            })
            .addCase(createChallenge.fulfilled, (state) => {
                state.challengeActionLoading = false;
            })
            .addCase(createChallenge.rejected, (state, action) => {
                state.challengeActionLoading = false;
                state.challengeError = action.payload as string;
            });

        builder
            .addCase(updateChallenge.pending, (state) => {
                state.challengeActionLoading = true;
                state.challengeError = null;
            })
            .addCase(updateChallenge.fulfilled, (state) => {
                state.challengeActionLoading = false;
            })
            .addCase(updateChallenge.rejected, (state, action) => {
                state.challengeActionLoading = false;
                state.challengeError = action.payload as string;
            });

        builder
            .addCase(submitChallengeEntry.pending, (state) => {
                state.challengeActionLoading = true;
                state.challengeError = null;
            })
            .addCase(submitChallengeEntry.fulfilled, (state) => {
                state.challengeActionLoading = false;
            })
            .addCase(submitChallengeEntry.rejected, (state, action) => {
                state.challengeActionLoading = false;
                state.challengeError = action.payload as string;
            });

        builder
            .addCase(fetchChallengeLeaderboard.pending, (state) => {
                state.challengeLoading = true;
                state.challengeError = null;
            })
            .addCase(fetchChallengeLeaderboard.fulfilled, (state, action) => {
                state.challengeLoading = false;
                state.challengeLeaderboard = action.payload || [];
            })
            .addCase(fetchChallengeLeaderboard.rejected, (state, action) => {
                state.challengeLoading = false;
                state.challengeError = action.payload as string;
            });

        builder
            .addCase(fetchChallengeInterests.pending, (state) => {
                state.challengeLoading = true;
                state.challengeError = null;
            })
            .addCase(fetchChallengeInterests.fulfilled, (state, action) => {
                state.challengeLoading = false;
                state.challengeInterests = action.payload || {count: 0, interests: []};
            })
            .addCase(fetchChallengeInterests.rejected, (state, action) => {
                state.challengeLoading = false;
                state.challengeError = action.payload as string;
            });

        builder
            .addCase(fetchUserChallengeInterest.pending, (state) => {
                state.challengeError = null;
            })
            .addCase(fetchUserChallengeInterest.fulfilled, (state, action) => {
                state.hasExpressedChallengeInterest = Boolean(action.payload?.expressed);
            })
            .addCase(fetchUserChallengeInterest.rejected, (state, action) => {
                state.challengeError = action.payload as string;
                state.hasExpressedChallengeInterest = false;
            });

        builder
            .addCase(expressChallengeInterest.pending, (state) => {
                state.challengeActionLoading = true;
                state.challengeError = null;
            })
            .addCase(expressChallengeInterest.fulfilled, (state) => {
                state.challengeActionLoading = false;
                state.hasExpressedChallengeInterest = true;
                if (state.challengeInterests) {
                    state.challengeInterests = {
                        ...state.challengeInterests,
                        count: Number(state.challengeInterests.count || 0) + 1,
                    };
                }
            })
            .addCase(expressChallengeInterest.rejected, (state, action) => {
                state.challengeActionLoading = false;
                state.challengeError = action.payload as string;
            });

        builder
            .addCase(fetchChallengeModerators.pending, (state) => {
                state.challengeLoading = true;
                state.challengeError = null;
            })
            .addCase(fetchChallengeModerators.fulfilled, (state, action) => {
                state.challengeLoading = false;
                state.challengeModerators = action.payload || [];
            })
            .addCase(fetchChallengeModerators.rejected, (state, action) => {
                state.challengeLoading = false;
                state.challengeError = action.payload as string;
            });

        builder
            .addCase(assignChallengeModerator.pending, (state) => {
                state.challengeModeratorActionLoading = true;
                state.challengeModeratorError = null;
            })
            .addCase(assignChallengeModerator.fulfilled, (state) => {
                state.challengeModeratorActionLoading = false;
            })
            .addCase(assignChallengeModerator.rejected, (state, action) => {
                state.challengeModeratorActionLoading = false;
                state.challengeModeratorError = action.payload as string;
            });

        builder
            .addCase(removeChallengeModerator.pending, (state) => {
                state.challengeModeratorActionLoading = true;
                state.challengeModeratorError = null;
            })
            .addCase(removeChallengeModerator.fulfilled, (state) => {
                state.challengeModeratorActionLoading = false;
            })
            .addCase(removeChallengeModerator.rejected, (state, action) => {
                state.challengeModeratorActionLoading = false;
                state.challengeModeratorError = action.payload as string;
            });

        builder
            .addCase(voteChallengeEntry.pending, (state) => {
                state.challengeVoteActionLoading = true;
                state.challengeVoteError = null;
            })
            .addCase(voteChallengeEntry.fulfilled, (state) => {
                state.challengeVoteActionLoading = false;
            })
            .addCase(voteChallengeEntry.rejected, (state, action) => {
                state.challengeVoteActionLoading = false;
                state.challengeVoteError = action.payload as string;
            });

        builder
            .addCase(disqualifyChallengeEntry.pending, (state) => {
                state.challengeDisqualifyActionLoading = true;
                state.challengeDisqualifyError = null;
            })
            .addCase(disqualifyChallengeEntry.fulfilled, (state) => {
                state.challengeDisqualifyActionLoading = false;
            })
            .addCase(disqualifyChallengeEntry.rejected, (state, action) => {
                state.challengeDisqualifyActionLoading = false;
                state.challengeDisqualifyError = action.payload as string;
            });

        builder
            .addCase(fetchDisqualifiedChallengeEntries.pending, (state) => {
                state.challengeLoading = true;
                state.challengeError = null;
            })
            .addCase(fetchDisqualifiedChallengeEntries.fulfilled, (state, action) => {
                state.challengeLoading = false;
                state.disqualifiedChallengeEntries = action.payload || [];
            })
            .addCase(fetchDisqualifiedChallengeEntries.rejected, (state, action) => {
                state.challengeLoading = false;
                state.challengeError = action.payload as string;
            });

        builder
            .addCase(reviewChallengeEntry.pending, (state) => {
                state.challengeReviewActionLoading = true;
                state.challengeReviewError = null;
            })
            .addCase(reviewChallengeEntry.fulfilled, (state) => {
                state.challengeReviewActionLoading = false;
            })
            .addCase(reviewChallengeEntry.rejected, (state, action) => {
                state.challengeReviewActionLoading = false;
                state.challengeReviewError = action.payload as string;
            });

        builder
            .addCase(fetchChallengeEntryReviews.pending, (state) => {
                state.challengeLoading = true;
                state.challengeError = null;
            })
            .addCase(fetchChallengeEntryReviews.fulfilled, (state, action) => {
                state.challengeLoading = false;
                state.challengeEntryReviewsByEntryId[action.meta.arg] = action.payload || [];
            })
            .addCase(fetchChallengeEntryReviews.rejected, (state, action) => {
                state.challengeLoading = false;
                state.challengeError = action.payload as string;
            });

        builder
            .addCase(fetchChallengeModerationLogs.pending, (state) => {
                state.challengeLoading = true;
                state.challengeError = null;
            })
            .addCase(fetchChallengeModerationLogs.fulfilled, (state, action) => {
                state.challengeLoading = false;
                state.challengeModerationLogs = action.payload || [];
            })
            .addCase(fetchChallengeModerationLogs.rejected, (state, action) => {
                state.challengeLoading = false;
                state.challengeError = action.payload as string;
            });
    }
});

export const {updateReel, clearSelectedChallenge} = communitySlice.actions;

export default communitySlice.reducer;
